/**
 * Feature flags for unfinished features. `VITE_FEATURE_<NAME>=true|false` at build time decides; without it a flag
 * is on in development and mock mode and off in production builds.
 */
import { MOCK } from "./env.ts";

function flag(value: string | undefined): boolean {
  if (value === "true") return true;
  if (value === "false") return false;
  return import.meta.env.DEV || MOCK;
}

export const featureFlags = {
  /** Huddles (voice/video calls): channel header button, sidebar bar, title-bar pill, call pane, settings. */
  huddles: flag(import.meta.env.VITE_FEATURE_HUDDLES),
  /**
   * Stickers and GIFs: the composer's Emoji · Stickers · GIFs picker, sticker creation, "Save as sticker" and pack
   * management. Off: the composer's emoji button opens the plain emoji picker (sticker/GIF messages still render).
   */
  stickers: flag(import.meta.env.VITE_FEATURE_STICKERS),
  /**
   * Channel management and sidebar folders: the "+" on Channels (create channel, browse, new folder), ⌘K commands,
   * folders with drag and drop and "Move to folder…", row menus (star, mute, leave), channel settings, join/archived
   * bars. Off: the sidebar looks and behaves as before (the API routes ship either way; they are additive).
   */
  channels: flag(import.meta.env.VITE_FEATURE_CHANNELS),
  /**
   * Composer autocomplete: @ people/agents/@here, # channels, : emoji, / commands, and the @ button opening the menu.
   * Off: no menu (ChannelViewModel never opens one) and the @ button just types "@", as before.
   */
  autocomplete: flag(import.meta.env.VITE_FEATURE_AUTOCOMPLETE),
  /**
   * Settings: the Settings tab (profile & photo, account deletion, workspace, members & roles, invitations, guests &
   * agents), "Profile & settings" in the profile menu, member profile cards and role badges. Off: no entry points
   * (`/{ws}/settings` shows a placeholder); the API routes ship either way (they are additive).
   */
  settings: flag(import.meta.env.VITE_FEATURE_SETTINGS),
  /**
   * Tab management: a split view is one joined tab that stays together; Chrome-style tab groups (name, colour,
   * collapse, drag in and out); drag to reorder or onto a tab / pane edge to split; pinned icon tabs; tab context menu
   * (duplicate, close others / to the right, move to a new window, copy link); ⌘⇧T reopens; overflow scrolling with tab
   * search (⌥⇧A); a tab switcher with group sections on narrow widths. Off: the original tab strip and split behaviour.
   */
  tabs: flag(import.meta.env.VITE_FEATURE_TABS),
  /**
   * Page sharing and permissions: the page top bar (share indicator, Share, favourite star, "…" menu with copy link,
   * move to, lock, export as Markdown, trash) on pages, databases and rows; the Share dialog (invite people, agents and
   * guests by email, people with access, general access, publish to the web); sidebar sharing badges; the public
   * /s/<token> page; file Share (post to a conversation, public file links); Settings → Sharing. Off: pages and files
   * look and behave as before (the API routes ship either way; they are additive).
   */
  sharing: flag(import.meta.env.VITE_FEATURE_SHARING),
  /**
   * Templates: the template gallery ("+" → Templates…, the Projects hub, ⌘K "New from template") with built-in and
   * saved templates, "Save as template" and "Duplicate" (page and sidebar menus), editing a template's page, and
   * database row templates ("New ▾", default template, defaults + body). Off: the sidebar "+" keeps Page / Database /
   * Projects / Meeting notes and databases keep the plain "New" (the API routes ship either way; they are additive).
   */
  templates: flag(import.meta.env.VITE_FEATURE_TEMPLATES),
} as const;
